import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useToast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './ImpactPage.css';

const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const ImpactReport = () => {
  const { toast } = useToast();
  const [showPurchase, setShowPurchase] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load Cashfree SDK once
  useEffect(() => {
    if (!document.getElementById('cashfree-sdk')) {
      const s = document.createElement('script');
      s.id = 'cashfree-sdk';
      s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  const metrics = [
    { value: '4+', label: 'Countries', icon: 'M12 2 L2 7 L12 12 L22 7 Z M2 17 L12 22 L22 17 M2 12 L12 17 L22 12' },
    { value: '\u20B92.62Cr+', label: 'Projects', icon: 'M3 3 L21 3 L21 21 L3 21 Z M3 9 L21 9 M9 3 L9 21' },
    { value: 'UNESCO', label: 'Aligned Initiative', icon: 'M12 2 L15 8 L22 9 L17 14 L18 21 L12 18 L6 21 L7 14 L2 9 L9 8 Z' },
    { value: 'Multi-Sector', label: 'Operations', icon: 'M3 12 L7 8 L11 12 L15 8 L19 12 L23 8 M3 18 L7 14 L11 18 L15 14 L19 18 L23 14' },
  ];

  const cases = [
    { name: 'Eco-Warriors\u2122', desc: "Africa's first UNESCO-patronaged educational mobile game. Deployed across 320+ schools in 4 African countries, reaching 89,000+ students with 39,000+ tablets distributed. Teaches recycling and waste management through gamification." },
    { name: 'Digital & Campaign Services', desc: "Full-service political and brand campaign management. Built for Maharashtra's electoral and commercial landscape — precision, reach, and regional intelligence." },
    { name: 'Discover Solapur\u2122', desc: 'A platform built to surface local culture, events, and experiences in Tier 2 India.' },
    { name: 'PrismaTex', desc: '20,000 sq ft manufacturing facility in Kumbhari, Solapur. 280 women members. \u20B92.62Cr NCDC-funded.' },
  ];

  const validatePhone = (phone) => {
    const cleaned = (phone || '').replace(/\s/g, '');
    return /^\+91[6-9]\d{9}$/.test(cleaned);
  };

  const extractMessage = (data, status) => {
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && detail.message) return detail.message;
    return `Payment temporarily unavailable. Please try again. (${status})`;
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.target);
    const phoneRaw = (fd.get('phone') || '').toString().trim();
    const phone = phoneRaw.startsWith('+') ? phoneRaw : `+91${phoneRaw.replace(/\D/g, '').slice(-10)}`;

    if (!validatePhone(phone)) {
      toast({ title: 'Invalid mobile number', description: 'Please enter a valid Indian mobile number (e.g. +919876543210).', variant: 'destructive' });
      setSubmitting(false);
      return;
    }

    const payload = {
      name: fd.get('name'),
      email: fd.get('email'),
      company: fd.get('company') || '',
      phone,
    };

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

    try {
      const res = await fetch(`${BACKEND_URL}/api/impact-report/cashfree/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }

      if (!res.ok) {
        throw new Error(extractMessage(data, res.status));
      }

      if (!window.Cashfree) {
        throw new Error('Payment temporarily unavailable. Please try again.');
      }

      const cashfree = window.Cashfree({ mode: data.mode || 'production' });
      cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: '_self',
      });
    } catch (err) {
      const raw = err.message || '';
      // Filter preview-environment fetch clone error noise
      const friendly = raw.includes("'clone'") || raw.includes('Response body')
        ? 'Payment temporarily unavailable. Please try again.'
        : raw || 'Payment temporarily unavailable. Please try again.';
      toast({ title: 'Unable to start payment', description: friendly, variant: 'destructive' });
      setSubmitting(false);
    }
  };

  return (
    <div className="impact-page" data-testid="impact-page">
      <SEO
        title="Prisma Impact | The Prisma Global"
        description="Prisma is not an idea. It is already in motion—across sectors, markets, and systems."
        canonical="/impact"
      />

      <Navbar />

      {/* Section 1: Opening */}
      <section className="impact-opening" data-testid="impact-opening">
        <div className="impact-opening-bg">
          <div className="impact-gradient-1" />
          <div className="impact-gradient-2" />
          <div className="impact-noise" />
        </div>
        <div className="impact-inner">
          <motion.p
            className="impact-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Prisma Impact
          </motion.p>
          <motion.h1
            className="impact-headline"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            Prisma is not an idea.
          </motion.h1>
          <motion.p
            className="impact-subline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            It is already in motion&mdash;across sectors, markets, and systems.
          </motion.p>
        </div>
      </section>

      {/* Section 2: Metrics */}
      <section className="impact-metrics" data-testid="impact-metrics">
        <div className="impact-inner">
          <div className="metrics-grid">
            {metrics.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="metric-block" data-testid={`impact-metric-${i}`}>
                  <svg className="metric-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d={m.icon} />
                  </svg>
                  <span className="metric-value">{m.value}</span>
                  <span className="metric-label">{m.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Cases */}
      <section className="impact-cases" data-testid="impact-cases">
        <div className="impact-inner">
          <Reveal>
            <p className="impact-section-label">In Motion</p>
          </Reveal>
          <div className="cases-grid">
            {cases.map((c, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="case-card" data-testid={`case-card-${i}`}>
                  <h3 className="case-name">{c.name}</h3>
                  <p className="case-desc">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Report CTA */}
      <section className="impact-report-cta" data-testid="impact-report-cta">
        <div className="impact-inner">
          <Reveal>
            <p className="impact-section-label">Full Report</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="report-cta-headline">Get the Full Prisma Impact Report</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="report-cta-body">A detailed breakdown of ventures, execution models, and system architecture.</p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="report-benefits" data-testid="report-benefits">
              <p className="report-benefits-title">What you get</p>
              <ul className="report-benefits-list">
                <li>Breakdown of real ventures and execution models</li>
                <li>How systems are built and scaled across sectors</li>
                <li>Strategic insights behind Prisma&rsquo;s ecosystem</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="report-cta-action">
              <button
                className="cta-primary large"
                data-testid="purchase-report-btn"
                onClick={() => setShowPurchase(true)}
              >
                Purchase Report &rarr;
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.35}>
            <div className="report-cta-meta" data-testid="report-cta-meta">
              <p className="report-cta-price">One-time access &middot; &#8377;999</p>
              <p className="report-audience-title">For:</p>
              <ul className="report-audience-list">
                <li>Founders building multi-venture ecosystems</li>
                <li>Operators scaling systems</li>
                <li>Strategic collaborators</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section 5: Closing */}
      <section className="impact-closing" data-testid="impact-closing">
        <div className="impact-inner">
          <Reveal>
            <h2 className="closing-line">This is not expansion.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="closing-accent">This is architecture.</p>
          </Reveal>
        </div>
      </section>

      {/* Purchase Modal */}
      {showPurchase && (
        <div className="purchase-overlay" data-testid="purchase-modal" onClick={() => !submitting && setShowPurchase(false)}>
          <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
            <button className="purchase-close" data-testid="purchase-close-btn" onClick={() => !submitting && setShowPurchase(false)} aria-label="Close">&times;</button>
            <p className="purchase-eyebrow">Prisma Impact Report</p>
            <h3 className="purchase-title">Complete Your Purchase</h3>
            <p className="purchase-amount">&#8377;999 <span className="purchase-amount-label">/ one-time</span></p>
            <p className="purchase-sub">Includes the full Prisma Impact Report &mdash; ventures, execution models, and system architecture.</p>
            <form className="purchase-form" onSubmit={handlePurchase} data-testid="purchase-form">
              <input name="name" placeholder="Full Name" required data-testid="purchase-name" disabled={submitting} />
              <input name="email" type="email" placeholder="Email" required data-testid="purchase-email" disabled={submitting} />
              <input name="phone" type="tel" placeholder="Mobile (+91XXXXXXXXXX)" required data-testid="purchase-phone" disabled={submitting} title="Indian mobile number, e.g. +919876543210" />
              <input name="company" placeholder="Company (optional)" data-testid="purchase-company" disabled={submitting} />
              <button type="submit" className="cta-primary large" data-testid="purchase-submit-btn" disabled={submitting}>
                {submitting ? 'Processing\u2026' : 'Pay \u20B9999 \u2192'}
              </button>
            </form>
            <p className="purchase-secure">Secure payment via Cashfree</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ImpactReport;
